import {
  Alert,
  Button,
  Container,
  Divider,
  Paper,
  PasswordInput,
  Stack,
  Text,
  TextInput,
  Title,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { notifications } from "@mantine/notifications";
import { IconCheck, IconLogin, IconUserPlus } from "@tabler/icons-react";
import { useMutation } from "@tanstack/react-query";
import { useState } from "react";
import { Link, useLocation } from "wouter";
import { authApi } from "../lib/api";

interface SignupFormData {
  email: string;
  password: string;
  confirmPassword: string;
}

export const SignupPage: React.FC = () => {
  const [, setLocation] = useLocation();
  const [error, setError] = useState<string | null>(null);
  const form = useForm<SignupFormData>({
    initialValues: {
      email: "",
      password: "",
      confirmPassword: "",
    },
    validate: {
      email: (value) => {
        if (!value) return "Email is required";
        if (!/^\S+@\S+\.\S+$/.test(value)) return "Please enter a valid email";
        return null;
      },
      password: (value) => {
        if (!value) return "Password is required";
        if (value.length < 8) return "Password must be at least 8 characters";
        if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(value)) {
          return "Password must contain at least one uppercase letter, one lowercase letter, and one number";
        }
        return null;
      },
      confirmPassword: (value, values) => {
        if (!value) return "Please confirm your password";
        if (value !== values.password) return "Passwords do not match";
        return null;
      },
    },
  });

  const signupMutation = useMutation({
    mutationFn: authApi.signup,
    onSuccess: (_data, variables) => {
      notifications.show({
        title: "Check your email",
        message: "We sent you a verification code to finish creating your account.",
        color: "blue",
        icon: <IconCheck size="1rem" />,
      });
      setLocation(`/confirm-signup?email=${encodeURIComponent(variables.email)}`);
    },
    onError: (err) => {
      const errorMessage = err instanceof Error ? err.message : "Failed to create account";
      setError(errorMessage);
    },
  });
  const handleSubmit = (values: SignupFormData) => {
    setError(null);
    signupMutation.mutate({
      email: values.email,
      password: values.password,
    });
  };

  return (
    <Container size="xs" py="xl">
      <Stack gap="xl">
        <Stack gap="xs" align="center">
          <Title order={2}>Create Account</Title>
          <Text c="dimmed" size="sm">
            Join MyAgilityQs to track your dog's agility progress
          </Text>
        </Stack>

        <Paper withBorder shadow="sm" p="xl" radius="md">
          <form onSubmit={form.onSubmit(handleSubmit)}>
            <Stack gap="md">
              {error && (
                <Alert color="red" variant="light">
                  {error}
                </Alert>
              )}{" "}
              <TextInput
                label="Email"
                placeholder="your@email.com"
                required
                autoComplete="email"
                {...form.getInputProps("email")}
              />
              <PasswordInput
                label="Password"
                placeholder="Create a strong password"
                required
                autoComplete="new-password"
                {...form.getInputProps("password")}
              />
              <PasswordInput
                label="Confirm Password"
                placeholder="Confirm your password"
                required
                autoComplete="new-password"
                {...form.getInputProps("confirmPassword")}
              />
              <Button
                type="submit"
                fullWidth
                size="lg"
                loading={signupMutation.isPending}
                leftSection={<IconUserPlus size="1.2rem" />}
              >
                Create Account
              </Button>

              <Divider label="Already have an account?" labelPosition="center" mt="xs" />

              <Button
                component={Link}
                href="/login"
                fullWidth
                size="lg"
                variant="light"
                color="blue"
                leftSection={<IconLogin size="1.2rem" />}
                disabled={signupMutation.isPending}
              >
                Log In
              </Button>
            </Stack>
          </form>
        </Paper>
      </Stack>
    </Container>
  );
};
